import { useState } from 'react';
import { Lightbulb } from 'lucide-react';
import { useCardStyles } from './cardStyles';
import { attrNumber, friendlyName, useEntity } from './useEntity';
import { useHAService } from '../../context/HAConnectionContext';

export interface LightCardProps {
  entityId: string;
  title?: string;
}

export function LightCard({ entityId, title }: LightCardProps): JSX.Element {
  const styles = useCardStyles();
  const entity = useEntity(entityId);
  const { callService } = useHAService();
  const isOn = entity?.state === 'on';
  const brightness = attrNumber(entity, 'brightness');
  const colorTemp = attrNumber(entity, 'color_temp');
  const [optimisticOn, setOptimisticOn] = useState<boolean | null>(null);

  const on = optimisticOn ?? isOn;
  const name = friendlyName(entity, title);

  const toggle = () => {
    const next = !on;
    setOptimisticOn(next);
    callService('light', next ? 'turn_on' : 'turn_off', { entity_id: entityId }).catch((err) => {
      console.error('[LightCard] toggle failed', entityId, err);
      setOptimisticOn(null);
    });
  };

  return (
    <div
      className={styles.container}
      data-domain="light"
      data-entity-id={entityId}
      data-state={on ? 'on' : 'off'}
    >
      <div className="flex items-center justify-between">
        <div
          className={`flex h-9 w-9 items-center justify-center rounded-xl transition-colors ${
            on
              ? 'bg-amber-400/20 text-amber-500 dark:text-amber-400'
              : styles.isDark
              ? 'bg-gray-700/60 text-gray-500'
              : 'bg-gray-100 text-gray-400'
          }`}
        >
          <Lightbulb className="h-5 w-5" />
        </div>
        <button
          type="button"
          onClick={toggle}
          aria-pressed={on}
          aria-label={name}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
            on ? 'bg-[var(--rsc-primary)]' : styles.isDark ? 'bg-gray-600' : 'bg-gray-300'
          }`}
        >
          <span
            aria-hidden
            className="pointer-events-none absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all"
            style={{ left: on ? 'calc(100% - 22px)' : '2px' }}
          />
        </button>
      </div>

      <div>
        <div className={`truncate text-sm font-semibold ${styles.title}`}>{name}</div>
        <div className={`text-xs ${styles.subtle}`}>{on ? 'An' : 'Aus'}</div>
      </div>

      {brightness !== undefined && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between text-xs">
            <span className={styles.label}>Helligkeit</span>
            <span className={`${styles.muted} tabular-nums`}>{Math.min(100, Math.round(brightness > 255 ? brightness / 255 : (brightness / 255) * 100))}%</span>
          </div>
          <div className={`h-1.5 w-full overflow-hidden rounded-full ${styles.isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
            <div
              className="h-full rounded-full bg-amber-400 transition-all"
              style={{ width: `${Math.min(100, Math.max(0, brightness > 255 ? brightness / 255 : (brightness / 255) * 100))}%` }}
            />
          </div>
        </div>
      )}

      {colorTemp !== undefined && (
        <div className="flex items-center justify-between text-xs">
          <span className={styles.label}>Farbtemperatur</span>
          <span className={`${styles.muted} tabular-nums`}>{Math.round(colorTemp)} mired</span>
        </div>
      )}
    </div>
  );
}
